/**
 * TrackedPropertiesRenderer - Minimal tracked properties configuration
 *
 * Clean design: compact key-value rows with inline add
 */

import { makeStyles, tokens, Text, Input, Button, Field } from '@fluentui/react-components';
import { AddRegular, DismissRegular } from '@fluentui/react-icons';
import { useState } from 'react';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalS,
    display: 'block',
  },
  propertyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalS,
  },
  propertyRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  keyField: {
    flex: '0 0 120px',
    minWidth: 0,
  },
  valueField: {
    flex: 1,
    minWidth: 0,
  },
  emptyState: {
    padding: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  addRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
});

export interface TrackedPropertiesRendererProps extends SettingRendererProps<Record<string, string>> {
  label?: string;
}

/**
 * TrackedPropertiesRenderer component for custom property tracking
 */
export const TrackedPropertiesRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Tracked Properties',
}: TrackedPropertiesRendererProps) => {
  const styles = useStyles();
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  // Don't render if setting is not supported
  if (!setting.isSupported) {
    return null;
  }

  const properties = setting.value ?? {};
  const propertyKeys = Object.keys(properties);

  const handlePropertyValueChange = (key: string, value: string) => {
    onChange({
      ...properties,
      [key]: value,
    });
  };

  const handleRemoveProperty = (key: string) => {
    const newProperties = { ...properties };
    delete newProperties[key];
    onChange(newProperties);
  };

  const handleAddProperty = () => {
    const trimmedKey = newKey.trim();
    if (!trimmedKey || properties[trimmedKey] !== undefined) {
      return;
    }

    onChange({
      ...properties,
      [trimmedKey]: newValue,
    });
    setNewKey('');
    setNewValue('');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAddProperty();
    }
  };

  return (
    <div className={styles.container}>
      <Text className={styles.label}>{label}</Text>

      {propertyKeys.length === 0 ? (
        <Text className={styles.emptyState}>No tracked properties configured.</Text>
      ) : (
        <div className={styles.propertyList}>
          {propertyKeys.map((key) => (
            <div key={key} className={styles.propertyRow}>
              <div className={styles.keyField}>
                <Field size="small">
                  <Input value={key} disabled aria-label={`Property key: ${key}`} />
                </Field>
              </div>
              <div className={styles.valueField}>
                <Field size="small">
                  <Input
                    value={properties[key] ?? ''}
                    onChange={(e) => handlePropertyValueChange(key, e.target.value)}
                    placeholder="${triggerBody()?.property}"
                    disabled={readOnly}
                    aria-label={`Value for ${key}`}
                  />
                </Field>
              </div>
              {!readOnly && (
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  size="small"
                  onClick={() => handleRemoveProperty(key)}
                  aria-label={`Remove property ${key}`}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className={styles.addRow}>
          <div className={styles.keyField}>
            <Field size="small">
              <Input
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="Key"
                onKeyDown={handleKeyDown}
                aria-label="New property key"
              />
            </Field>
          </div>
          <div className={styles.valueField}>
            <Field size="small">
              <Input
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="Value expression"
                onKeyDown={handleKeyDown}
                aria-label="New property value"
              />
            </Field>
          </div>
          <Button
            appearance="primary"
            icon={<AddRegular />}
            size="small"
            onClick={handleAddProperty}
            disabled={!newKey.trim()}
            aria-label="Add property"
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

TrackedPropertiesRenderer.displayName = 'TrackedPropertiesRenderer';

// Register this renderer
registerSettingRenderer('trackedProperties', TrackedPropertiesRenderer);
